import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, countIn, craft, editorial, showroom } from '../../data/collections';
import { pillars, stats, testimonials } from '../../data/content';
import { brand, site } from '../../data/site';
import { useEnquiry } from '../../context/EnquiryContext';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import { IconClock, IconMail, IconPhone, IconPin, Lotus } from '../ui/Icons';

/* ------------------------------------------------------------------ */
/*  Announcement marquee — one hairline of quiet text                  */
/* ------------------------------------------------------------------ */
export function Marquee() {
  const words = [
    'BIS Hallmarked · HUID on every piece',
    'Bridal & Everyday Gold',
    'Antique · Bridal · Polki · Diamond',
    // Drops out of the rotation when the promotion ends.
    site.offer.active && site.offer.headline,
    'Custom Orders Welcome',
    'Ahmedabad, Since 1997',
  ].filter(Boolean);
  const run = [...words, ...words];

  return (
    <div className="overflow-hidden border-b border-royal/10 bg-ivory py-4">
      <div className="flex w-max animate-marquee items-center">
        {run.map((w, i) => (
          <span key={i} className="flex items-center whitespace-nowrap">
            <span className="px-8 text-[11.5px] uppercase tracking-luxe text-royal/80">{w}</span>
            <span className="h-1 w-1 shrink-0 rotate-45 bg-gold/50" />
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Legacy — the founding story                                        */
/* ------------------------------------------------------------------ */
export function Legacy() {
  return (
    <section className="bg-ivory py-24 lg:py-36">
      <div className="shell grid items-start gap-16 lg:grid-cols-[1fr_0.85fr] lg:gap-24">
        <div>
          <SectionHeading
            index="01"
            eyebrow="The firm"
            title={'Bought right,\nmade properly'}
            lede="Nearly three decades of buying gold well and finishing it by hand — and quoting the same honest number to every family who walks in. That discipline is the whole business."
          />

          <Reveal delay={120} className="mt-14 grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="border-t border-royal/15 pt-4">
                <p className="font-display text-[2.2rem] leading-none text-royal">
                  {s.value}
                  {s.suffix && <span className="text-base text-gold">{s.suffix}</span>}
                </p>
                <p className="mt-2.5 text-[11.5px] uppercase tracking-wider2 text-royal/80">{s.label}</p>
              </div>
            ))}
          </Reveal>

          <Reveal delay={200} className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4">
            <Link to="/about" className="btn-line">
              <span className="btn-line-rule" />
              Our story
            </Link>
          </Reveal>
        </div>

        <Reveal delay={100} className="relative">
          <div className="wipe relative aspect-[4/5] overflow-hidden">
            <img
              src={showroom.counter}
              alt="Inside the Pramukh Jewellers showroom"
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
          <p className="mt-4 flex items-baseline justify-between border-t border-royal/10 pt-3 text-[11.5px] uppercase tracking-wider2 text-royal/75">
            <span>The showroom</span>
            <span className="text-gold-600">Science City Road</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Featured collections                                               */
/* ------------------------------------------------------------------ */
export function FeaturedCollections() {
  const show = categories.slice(0, 6);

  return (
    <section className="border-t border-royal/10 bg-cream py-24 lg:py-36">
      <div className="shell">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading index="02" eyebrow="Collections" title="What we make" />
          <Reveal delay={100} className="shrink-0">
            <Link to="/collections" className="btn-line">
              <span className="btn-line-rule" />
              All twelve
            </Link>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-x-5 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {show.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 90}>
              <Link to={`/collections/${c.slug}`} className="group block">
                <div className="wipe relative aspect-[4/5] overflow-hidden bg-beige/60">
                  <img
                    src={c.cover}
                    alt={c.name}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-[1500ms] ease-luxe group-hover:scale-[1.06]"
                  />
                </div>

                <div className="mt-4 flex items-start justify-between gap-4 border-t border-royal/10 pt-4">
                  <div>
                    <p className="text-[11.5px] tracking-wider2 text-gold-600">
                      {String(i + 1).padStart(2, '0')} — {c.native}
                    </p>
                    <h3 className="mt-2 font-display text-[1.6rem] leading-tight text-royal transition-colors duration-500 group-hover:text-gold-600">
                      {c.name}
                    </h3>
                    <p className="mt-1.5 text-[12px] uppercase tracking-wider2 text-royal/75">
                      {c.tagline}
                    </p>
                  </div>
                  <span className="mt-1 shrink-0 text-[11.5px] uppercase tracking-wider2 text-royal/70">
                    {countIn(c.slug)}
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Craftsmanship                                                      */
/* ------------------------------------------------------------------ */
export function Craftsmanship() {
  const steps = [
    { n: '01', t: 'Drawn', d: 'A sketch on paper, or a photograph you bring in. Colourways picked before a gram is melted.' },
    { n: '02', t: 'Struck', d: 'Nakashi worked into matte 22K — the relief cut deep so the motif still reads at arm’s length.' },
    { n: '03', t: 'Set', d: 'Kundan and polki seated by hand in gold foil. Meena enamel fired in small batches.' },
    { n: '04', t: 'Strung', d: 'Pearl, ruby and emerald strands knotted to length, then balanced so the piece hangs true.' },
  ];

  return (
    <section className="border-t border-royal/10 bg-ivory py-24 lg:py-36">
      <div className="shell grid gap-16 lg:grid-cols-[0.9fr_1fr] lg:items-start lg:gap-24">
        <Reveal className="order-2 space-y-5 lg:order-1">
          <div className="wipe relative aspect-[4/5] overflow-hidden">
            <img
              src={craft.beadwork}
              alt="Ruby bead and gold components laid out during design"
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div className="wipe relative aspect-square overflow-hidden">
              <img src={craft.sketch} alt="Design sketches and swatches" loading="lazy" className="h-full w-full object-cover" />
            </div>
            <div className="wipe relative aspect-square overflow-hidden">
              <img src={editorial.modernPolki} alt="The art of modern polki" loading="lazy" className="h-full w-full object-cover" />
            </div>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <SectionHeading
            index="03"
            eyebrow="Parampara · પરંપરા"
            title={'Made the slow way,\non purpose'}
            lede="Machines finish gold faster. They cannot give it the soft, shadowed surface that makes a piece look like it has been in the family for forty years."
          />

          <div className="mt-14">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 90} className="group grid grid-cols-[auto_1fr] gap-x-8 border-t border-royal/10 py-6 last:border-b">
                <span className="pt-1 text-[12px] tracking-wider2 text-gold-600">{s.n}</span>
                <div>
                  <h3 className="font-display text-[1.45rem] leading-tight text-royal">{s.t}</h3>
                  <p className="mt-2 max-w-md text-[14.5px] font-light leading-[1.75] text-royal/70">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Signature — Maharani Heritage                                      */
/* ------------------------------------------------------------------ */
export function Signature() {
  return (
    <section className="relative overflow-hidden bg-royal-900">
      <div className="absolute inset-0">
        <img
          src="/assets/gallery/post-07-4.jpg"
          alt=""
          loading="lazy"
          className="h-full w-full object-cover object-center opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-royal-900 via-royal-900/90 to-royal-900/55" />
      </div>

      <div className="shell relative grid gap-16 py-24 lg:grid-cols-2 lg:items-center lg:py-36">
        <div>
          <SectionHeading
            index="04"
            tone="light"
            eyebrow="Signature collection"
            title={'महारानी\nHeritage'}
            lede="Our house collection — broad nakashi collars, green meena medallions and kundan set by hand, for brides who want the weight of the old work without the fussiness."
          />

          <Reveal delay={140} className="mt-12">
            {['Matte 22K nakashi', 'Green meena enamel, fired in batches', 'Kundan and uncut polki', 'Choker, haram, jhumka and kada matched'].map(
              (f, i) => (
                <p
                  key={f}
                  className="grid grid-cols-[auto_1fr] gap-x-6 border-t border-cream/10 py-3.5 text-[14px] font-light text-cream/80 last:border-b"
                >
                  <span className="text-[12px] tracking-wider2 text-gold/50">0{i + 1}</span>
                  {f}
                </p>
              )
            )}
          </Reveal>

          <Reveal delay={200} className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4">
            <Link to="/collections/bridal" className="btn-line-light">
              <span className="btn-line-rule" />
              View bridal sets
            </Link>
            <Link
              to="/collections/antique"
              className="text-[12px] font-medium uppercase tracking-luxe text-cream/70 transition-colors duration-500 hover:text-gold-200"
            >
              Antique &amp; jadau
            </Link>
          </Reveal>
        </div>

        <Reveal className="grid grid-cols-3 gap-3 lg:gap-4">
          {['/assets/gallery/post-13-2.jpg', '/assets/gallery/post-02.jpg', '/assets/gallery/post-09-5.jpg'].map(
            (src, i) => (
              <div key={src} className={`wipe relative overflow-hidden ${i === 1 ? 'mt-10 lg:mt-16' : ''}`}>
                <div className="aspect-[3/4]">
                  <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
                </div>
              </div>
            )
          )}
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Why us                                                             */
/* ------------------------------------------------------------------ */
export function Pillars() {
  return (
    <section className="bg-cream py-24 lg:py-36">
      <div className="shell">
        <SectionHeading index="06" eyebrow="Why Pramukh" title="What you are actually buying" />

        <div className="mt-16 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 90} className="border-t border-royal/15 pt-5">
              <span className="text-[12px] tracking-wider2 text-gold-600">0{i + 1}</span>
              <h3 className="mt-4 font-display text-[1.35rem] leading-snug text-royal">{p.title}</h3>
              <p className="mt-3 text-[14px] font-light leading-[1.75] text-royal/70">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Testimonials                                                       */
/* ------------------------------------------------------------------ */
export function Testimonials() {
  const [active, setActive] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    const id = setInterval(() => {
      if (!paused.current) setActive((i) => (i + 1) % testimonials.length);
    }, 7500);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      className="bg-cream py-24 lg:py-36"
      onMouseEnter={() => {
        paused.current = true;
      }}
      onMouseLeave={() => {
        paused.current = false;
      }}
    >
      <div className="shell grid gap-16 lg:grid-cols-[0.72fr_1fr] lg:items-center lg:gap-24">
        <Reveal className="wipe relative aspect-[4/5] overflow-hidden">
          <img
            src={showroom.tryOn}
            alt="A customer trying on jewellery at the showroom"
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </Reveal>

        <div>
          <SectionHeading index="07" eyebrow="In their words" title="The people across the counter" />

          <div className="relative mt-12 min-h-[230px]">
            {testimonials.map((t, i) => (
              <blockquote
                key={t.quote}
                className={`transition-all duration-700 ease-luxe ${
                  i === active ? 'relative opacity-100' : 'pointer-events-none absolute inset-0 translate-y-3 opacity-0'
                }`}
                aria-hidden={i !== active}
              >
                <p className="font-display text-[1.35rem] font-light leading-[1.55] text-royal sm:text-[1.7rem]">
                  {t.quote}
                </p>
                <footer className="mt-7 flex items-center gap-4">
                  <span className="h-px w-8 bg-gold/50" />
                  <span>
                    <span className="block text-[13px] text-royal">{t.name}</span>
                    <span className="mt-0.5 block text-[11.5px] uppercase tracking-wider2 text-royal/75">
                      {t.meta}
                    </span>
                  </span>
                </footer>
              </blockquote>
            ))}
          </div>

          <div className="mt-10 flex gap-2.5">
            {testimonials.map((t, i) => (
              <button
                key={t.name}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Testimonial ${i + 1}`}
                aria-current={i === active}
                className="py-2"
              >
                <span
                  className={`block h-px transition-all duration-500 ${
                    i === active ? 'w-12 bg-gold' : 'w-6 bg-royal/15'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Visit us + map                                                     */
/* ------------------------------------------------------------------ */
export function VisitUs() {
  const { openEnquiry } = useEnquiry();

  const rows = [
    {
      icon: IconPin,
      label: 'Address',
      body: (
        <a href={site.social.maps} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-gold-600">
          {site.address.line1}, {site.address.line2},<br />
          {site.address.city}, {site.address.state} {site.address.pin}
        </a>
      ),
    },
    {
      icon: IconPhone,
      label: 'Office',
      body: (
        <a href={`tel:${site.phone}`} className="transition-colors hover:text-gold-600">
          {site.phoneDisplay}
        </a>
      ),
    },
    {
      icon: IconMail,
      label: 'Email',
      body: (
        <a href={`mailto:${site.email}`} className="break-all transition-colors hover:text-gold-600">
          {site.email}
        </a>
      ),
    },
    {
      icon: IconClock,
      label: 'Hours',
      body: site.hours.map((h) => (
        <span key={h.days} className="block">
          {h.days} · <span className="text-royal">{h.time}</span>
        </span>
      )),
    },
  ];

  return (
    <section className="border-t border-royal/10 bg-ivory py-24 lg:py-36">
      <div className="shell">
        <SectionHeading
          index="08"
          eyebrow="Visit"
          title={'Fortune Business Hub,\nSola'}
          lede="Come and see the weight in your hand. Bring a photograph of what you have in mind."
        />

        <div className="mt-16 grid gap-12 lg:grid-cols-[0.62fr_1.38fr] lg:gap-16">
          <div>
            {rows.map(({ icon: Icon, label, body }) => (
              <Reveal key={label} className="grid grid-cols-[auto_1fr] gap-x-6 border-t border-royal/10 py-5 last:border-b">
                <Icon className="mt-1 h-4 w-4 text-gold/70" />
                <div>
                  <p className="text-[11.5px] uppercase tracking-wider2 text-royal/80">{label}</p>
                  <div className="mt-2 text-[14px] font-light leading-relaxed text-royal/70">{body}</div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={120} className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-4">
              <button type="button" onClick={() => openEnquiry('', 'manual')} className="btn-line">
                <span className="btn-line-rule" />
                Send an enquiry
              </button>
              <a
                href={site.social.maps}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[12px] font-medium uppercase tracking-luxe text-royal/80 transition-colors duration-500 hover:text-gold-600"
              >
                Get directions
              </a>
            </Reveal>

            <Reveal delay={180} className="mt-10 flex items-center gap-5 border-t border-royal/10 pt-8">
              <img src={brand.qrLocation} alt="Scan for showroom directions" className="h-20 w-20" />
              <p className="text-[12.5px] font-light leading-relaxed text-royal/70">
                Scan to open the showroom
                <br />
                in Google Maps.
              </p>
              <Lotus className="ml-auto hidden h-5 w-auto text-gold/40 sm:block" />
            </Reveal>
          </div>

          {/* Map is the anchor of this section — full colour, tall, and framed
              so the unit is easy to spot. */}
          <Reveal delay={100} className="relative min-h-[440px] border border-royal/10 bg-beige/50 lg:min-h-[660px]">
            <iframe
              title="Pramukh Jewellers on Google Maps"
              src={site.mapEmbed}
              className="absolute inset-0 h-full w-full"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
